import { Component, Output, Input, OnInit, EventEmitter } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { Observable, map, startWith } from 'rxjs';
import { FlightsFormData } from 'src/app/flight/models/FlightsFormData';
import { getItemFromSessionStorage, FLIGHT_FORM_DATA } from 'src/app/utils/SessionStorageUtil';
import { Airport, Airports } from 'src/app/flight/models/Airport';
import { normalizeValue } from 'src/app/utils/StringUtil';
import { PasseLivreService } from 'src/app/core/services/passe-livre/passe-livre.service';
import moment from 'moment';
import { configs } from 'src/configs/geral';

enum CheckboxOption {
  ONE_WAY='ONE_WAY',
  ROUND_TRIP='ROUND_TRIP',
  NONE='NONE'
}

function PeriodValidator(): ValidatorFn  {
  return (group: AbstractControl): ValidationErrors | null  => {
    const roundTripChecked = group.get('option')?.value === CheckboxOption.ROUND_TRIP;
    const dtDeparture =group.get('dtDeparture')?.value;
    const dtArrival =group.get('dtArrival')?.value;
  
    if (roundTripChecked && (dtArrival && dtDeparture)) {
      if (moment(dtDeparture).isAfter(moment(dtArrival))) {
        return { periodInvalid: true };
      }
    }
    return null;
  }
}

function DateArrivalValidatorRequired(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null  => {
    const roundTripChecked = group.get('option')?.value === CheckboxOption.ROUND_TRIP;
    const dtArrival = group.get('dtArrival')?.value;
  
    if (roundTripChecked && !dtArrival) {
        return {
          dtArrivalRequired: true
        }
    }
    return null;
  }
}

function RouteValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null  => {
    const airportOrigin = group.get('airportOrigin')?.value;
    const airportDestiny = group.get('airportDestiny')?.value;
  
    if ((airportOrigin && airportDestiny) && (airportOrigin === airportDestiny)) {
        return {
          routeInvalid: true
        }
    }
    return null;
  }
}

@Component({
    selector: 'app-flights-form',
    templateUrl: './flights-form.component.html',
    styleUrls: ['./flights-form.component.scss'],
    standalone: false
})
export class FlightsFormComponent implements OnInit {
  
  @Input() form: FormGroup;
  @Output() onSubmit: EventEmitter<any> = new EventEmitter();
  @Output() filled: EventEmitter<boolean> = new EventEmitter();

  CheckboxOption = CheckboxOption;
  currentlyChecked: CheckboxOption = CheckboxOption.ONE_WAY;
  
  submited: boolean = false;
  
  minDate: Date;
  maxDateOutbound: Date;
  maxDateInbound: Date;

  airports: Airport[] = [];
  filteredAirportsOrigin: Observable<Airport[]>;
  filteredAirportsDestiny: Observable<Airport[]>;

  constructor(private passeLivreService: PasseLivreService, private dateAdapter: DateAdapter<any>) {}

  ngOnInit() {
    this.dateAdapter.setLocale('pt-br');
    this.getAirports();
    this.buildFormGroup();
    this.fillFormWithDataFromStorage();

    const today = new Date();
    this.minDate = today;
    this.maxDateOutbound = moment(today).add(configs.maximumDaysOutbound, 'days').toDate();
    this.maxDateInbound = moment(today).add(configs.maximumDaysInbound, 'days').toDate();

  }

  getAirports(): void {
    this.passeLivreService.getAirportsBr().subscribe({
      next: (response: Airports) => {
        this.airports = response.airport;
      },
      error: () => {}
    })
  }

  isAirportValid = (control: AbstractControl): ValidationErrors | null => {   
    const onlyAirports = this.airports.map(airport => airport.airportInfo.iataCode);
    
    let originAirportIsValid = onlyAirports.includes(control.get('airportOrigin')?.value);
    let destinyAirportIsValid = onlyAirports.includes(control.get('airportDestiny')?.value);

    return this.airports.length == 0 || (originAirportIsValid && destinyAirportIsValid) ? null : { invalidOption: true };
    
  };

  buildFormGroup(): void {
    this.form.addControl('option', new FormControl(CheckboxOption.ONE_WAY, Validators.required));
    this.form.addControl('airportOrigin', new FormControl('', Validators.required));
    this.form.addControl('airportDestiny', new FormControl('', Validators.required));
    this.form.addControl('dtDeparture', new FormControl('', Validators.required));
    this.form.addControl('dtArrival', new FormControl(''));

    this.form.setValidators([PeriodValidator(), DateArrivalValidatorRequired(), RouteValidator(), this.isAirportValid]);

    const airportOrigin = this.form.get('airportOrigin');
    const airportDestiny = this.form.get('airportDestiny');

    if (airportOrigin) {
      this.filteredAirportsOrigin = airportOrigin.valueChanges.pipe(
        startWith(''),
        map(value => this.filter(value)),
      );
    }

    if (airportDestiny) {
      this.filteredAirportsDestiny = airportDestiny.valueChanges.pipe(
        startWith(''),
        map(value => this.filter(value)),
      );
    }
  }

  private filter(value: string): Airport[] {
    const filterValue = normalizeValue(value);

    return this.airports.filter((airport: Airport) => {
      const iataCodeValueNormalize = normalizeValue(airport.airportInfo.iataCode);
      const cityNameValueNormalize = normalizeValue(airport.city.name);
      return iataCodeValueNormalize.includes(filterValue) || cityNameValueNormalize.includes(filterValue);
    });
  }

  selectCheckbox(targetType: CheckboxOption): void {
    if (targetType !== CheckboxOption.ROUND_TRIP) {
      this.form.get('dtArrival')?.setValue('');
    }

    if(this.currentlyChecked === targetType) {
      this.form.get('option')?.setValue(null);
      this.currentlyChecked = CheckboxOption.NONE;
      return;
    }

    this.currentlyChecked = targetType;
    this.form.get('option')?.setValue(targetType);
  }

  /** Exchanges the IATA codes held by the two controls; dates and trip type are untouched. */
  swapRoute(): void {
    const origin = this.form.get('airportOrigin');
    const destiny = this.form.get('airportDestiny');
    if (!origin || !destiny || (!origin.value && !destiny.value)) return;

    const previousOrigin = origin.value ?? '';
    origin.setValue(destiny.value ?? '');
    destiny.setValue(previousOrigin);
  }

  submit(): void {
    this.onSubmit.emit();
  }

  fillFormWithDataFromStorage(): void {
    const flightFormData = getItemFromSessionStorage<FlightsFormData>(FLIGHT_FORM_DATA);

    if (flightFormData) {
      this.setValuesInForm(this.form, flightFormData);
      this.currentlyChecked = this.form.get('option')?.value;
      this.filled.emit(true);
    }
  }

  setValuesInForm(form: FormGroup, data: FlightsFormData): void {
    Object.keys(data).forEach((key: string) => {
      form.get(key)?.setValue(data[key as keyof FlightsFormData]);
    });
  }
  
}
